import { Box } from "@mui/material";
import {
  BookmarkBorder as SavedIcon,
  RestaurantOutlined as MealIcon,
  ChevronLeft as PickIcon,
} from "@mui/icons-material";
import { useState } from "react";
import { v2 } from "../ui/v2Tokens";
import V2Dialog from "../ui/V2Dialog";
import { Pill, PrimaryButton, TextButton, EmptyState } from "../ui/primitives";
import FoodRowCore from "../builder/FoodRowCore";
import { useSavedMealsV2 } from "../data/useNutritionV2";
import { cloneItems, listTotals, type FoodItemV2 } from "../model/nutritionV2Model";
import { energyText, macroText } from "../ui/nutritionUnits";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";

type Mode = "saved" | "copy";

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  /** what to call the thing being created — "ארוחה" / "ארוחה חלופית" */
  entityLabel?: string;
  /** offered as the "copy from the main meal" source; omit to hide that mode */
  copyFrom?: FoodItemV2[];
  /** offer "create it empty and fill it in place" */
  allowEmpty?: boolean;
  onConfirm: (result: { name: string; items: FoodItemV2[] }) => void;
  defaultName?: string;
};

/**
 * A PICKER, not a builder: choose a company saved meal, or copy the meal this belongs
 * to. Foods are never composed in here — a meal is built inside the menu and saved to
 * the library from there, so there is exactly one place that edits a meal's contents.
 */
const PickSavedMealDialog = ({
  open,
  onClose,
  title,
  entityLabel = "ארוחה",
  copyFrom,
  allowEmpty,
  onConfirm,
  defaultName = "",
}: Props) => {
  const { data: savedMeals, loading } = useSavedMealsV2();
  const [mode, setMode] = useState<Mode>("saved");
  // calories or points — not to be confused with `mode`, which is which list the dialog shows
  const units = useNutritionDisplayMode();

  const close = () => {
    setMode("saved");
    onClose();
  };

  const confirm = (result: { name: string; items: FoodItemV2[] }) => {
    onConfirm(result);
    setMode("saved");
  };

  return (
    <V2Dialog
      open={open}
      onClose={close}
      title={title}
      subtitle={
        copyFrom ? "בחר ארוחה שמורה של החברה, או העתק מהארוחה המרכזית" : "בחר ארוחה שמורה של החברה"
      }
      actions={
        <>
          <TextButton onClick={close}>ביטול</TextButton>
          <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {allowEmpty && (
              <TextButton
                onClick={() => confirm({ name: defaultName || `${entityLabel} חדשה`, items: [] })}
              >
                צור ריקה ומלא כאן
              </TextButton>
            )}
            {mode === "copy" && (
              <PrimaryButton
                disabled={!copyFrom || copyFrom.length === 0}
                onClick={() =>
                  confirm({
                    name: defaultName || `${entityLabel} חדשה`,
                    items: cloneItems(copyFrom || []),
                  })
                }
              >
                העתק
              </PrimaryButton>
            )}
          </Box>
        </>
      }
    >
      {copyFrom && (
        <Box sx={{ display: "flex", gap: "6px", marginBottom: "14px", flexWrap: "wrap" }}>
          <Pill active={mode === "saved"} onClick={() => setMode("saved")}>
            ארוחה שמורה
          </Pill>
          <Pill active={mode === "copy"} onClick={() => setMode("copy")}>
            העתק מהארוחה המרכזית
          </Pill>
        </Box>
      )}

      {mode === "saved" &&
        (loading ? (
          // the library is fetched, so "none yet" has to wait until we actually know
          <EmptyState icon={SavedIcon} text="טוען ארוחות שמורות…" />
        ) : savedMeals.length === 0 ? (
          <EmptyState
            icon={SavedIcon}
            text='אין ארוחות שמורות עדיין. בנה ארוחה בתפריט ושמור אותה דרך "שמור כארוחה שמורה" בתפריט הפעולות שלה.'
          />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {savedMeals.map((saved) => {
              const totals = listTotals(saved.items);
              return (
                <Box
                  key={saved.id}
                  component="button"
                  type="button"
                  onClick={() => confirm({ name: saved.name, items: cloneItems(saved.items) })}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "10px",
                    width: "100%",
                    textAlign: "start",
                    padding: "11px 14px",
                    borderRadius: v2.rSm,
                    border: `1.5px solid ${v2.line}`,
                    background: v2.surface,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    "&:hover": { borderColor: v2.brand, background: v2.brandWash },
                  }}
                >
                  <Box>
                    <Box sx={{ fontSize: "13px", fontWeight: 700, color: v2.ink }}>{saved.name}</Box>
                    <Box sx={{ fontSize: "11px", color: v2.ink2, marginTop: "2px" }}>
                      {saved.items.length} פריטים · {energyText(totals.calories, units)} · חלבון {macroText("protein", totals.protein, units)}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      fontSize: "11px",
                      color: v2.brandText,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    בחר
                    <PickIcon sx={{ fontSize: 16 }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}

      {mode === "copy" &&
        (copyFrom && copyFrom.length > 0 ? (
          <Box sx={{ border: `1px solid ${v2.line}`, borderRadius: v2.rSm, overflow: "hidden" }}>
            {/* No handlers: this is a PREVIEW of what will be copied, so the quantity and the
                serving read as text and there is no trash. They used to render as a live input, a
                live dropdown and a live trash wired to `() => undefined` — three controls that did
                nothing when pressed. The alternative's own rows, one click later, are editable. */}
            {copyFrom.map((item) => (
              <FoodRowCore key={item.uid} item={item} compact />
            ))}
          </Box>
        ) : (
          <EmptyState icon={MealIcon} text="הארוחה המרכזית ריקה — אין מה להעתיק." />
        ))}
    </V2Dialog>
  );
};

export default PickSavedMealDialog;
